import { useQueries } from "@tanstack/react-query";
import type { AssistantConversation } from "@rivets/core";
import { client } from "@/lib/api";
import { qk } from "@/lib/query-keys";
import { relativeAssistantTime } from "./assistantStore";
import { conversationAwaitingApproval } from "./assistantApproval";

/**
 * The drawer's conversation list: title, when it last moved, and a dot while a
 * turn is running. Each row is one chat, so its status comes from the ordinary
 * chat detail query the org socket already patches live.
 */
export function AssistantConversationList({
  conversations,
  activeChatId,
  onSelect,
  onArchive,
  archiveDisabled,
}: {
  conversations: AssistantConversation[];
  activeChatId?: string;
  archiveDisabled?: boolean;
  onSelect: (chatId: string) => void;
  onArchive: (chatId: string) => void;
}) {
  const chatQueries = useQueries({
    queries: conversations.map((conversation) => ({
      queryKey: qk.chat(conversation.chatId),
      queryFn: () => client.chatDetail(conversation.chatId),
      staleTime: 30_000,
    })),
  });

  return (
    <ul className="assistant-list">
      {conversations.map((conversation, index) => {
        const status = chatQueries[index]?.data?.chat.status;
        const running = status === "running" || status === "queued";
        const awaiting = conversationAwaitingApproval(conversation, status);
        const activity = conversation.lastActivityAt ?? conversation.createdAt;
        return (
          <li key={conversation.chatId} className="assistant-list-item">
            <button
              type="button"
              className={`assistant-list-row${conversation.chatId === activeChatId ? " active" : ""}`}
              onClick={() => onSelect(conversation.chatId)}
            >
              <span className="assistant-list-title truncate">{conversation.title || "Conversation"}</span>
              {conversation.channel ? (
                <span className="assistant-list-channel">via {conversation.channel.provider}</span>
              ) : null}
              {awaiting ? <span className="assistant-list-badge" title="Started from a channel message; approve it before the assistant runs">Needs approval</span> : null}
              {running ? <span className="assistant-running-dot" title="Working" /> : null}
              <span className="assistant-list-time">{relativeAssistantTime(activity)}</span>
            </button>
            <button
              type="button"
              className="assistant-list-archive"
              aria-label={`Archive ${conversation.title || "conversation"}`}
              // An unapproved conversation is dismissed by rejecting it.
              title={awaiting ? "Reject this conversation to dismiss it" : undefined}
              disabled={archiveDisabled || awaiting}
              onClick={() => onArchive(conversation.chatId)}
            >
              ×
            </button>
          </li>
        );
      })}
    </ul>
  );
}
